import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { AssistantChatMessage } from "./models/AssistantChatMessage";
import { SchedulePlan } from "./models/SchedulePlan";
import { PlanResult } from "./models/PlanResult";
import {
  confirmSchedulePlan,
  sendAssistantMessage,
} from "./slice-extensions/assistant-schedule-slice-extension";

export interface AssistantScheduleState {
  messages: AssistantChatMessage[];
  pendingPlan: SchedulePlan | null;
  selectedTempIds: string[];
  result: PlanResult | null;
  sendStatus: LoadingStatus;
  confirmStatus: LoadingStatus;
  statusMessage: string | undefined;
}

const initialState: AssistantScheduleState = {
  messages: [],
  pendingPlan: null,
  selectedTempIds: [],
  result: null,
  sendStatus: LoadingStatus.Idle,
  confirmStatus: LoadingStatus.Idle,
  statusMessage: undefined,
};

const assistantScheduleSlice = createSlice({
  name: "assistantSchedule",
  initialState,
  reducers: {
    togglePlanAction: (state, action: PayloadAction<string>) => {
      const tempId = action.payload;
      state.selectedTempIds = state.selectedTempIds.includes(tempId)
        ? state.selectedTempIds.filter((id) => id !== tempId)
        : [...state.selectedTempIds, tempId];
    },
    dismissPlan: (state) => {
      state.pendingPlan = null;
      state.selectedTempIds = [];
    },
    resetConversation: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendAssistantMessage.pending, (state, action) => {
        state.messages.push({ role: "user", text: action.meta.arg });
        state.sendStatus = LoadingStatus.Loading;
        state.statusMessage = undefined;
        state.pendingPlan = null;
        state.selectedTempIds = [];
        state.result = null;
      })
      .addCase(sendAssistantMessage.fulfilled, (state, action) => {
        state.sendStatus = LoadingStatus.Idle;
        state.messages.push({ role: "assistant", text: action.payload.reply });
        state.pendingPlan = action.payload.plan;
        state.selectedTempIds = action.payload.plan
          ? action.payload.plan.actions.map((planAction) => planAction.tempId)
          : [];
      })
      .addCase(sendAssistantMessage.rejected, (state, action) => {
        state.sendStatus = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "The assistant could not answer";
      })
      .addCase(confirmSchedulePlan.pending, (state) => {
        state.confirmStatus = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(confirmSchedulePlan.fulfilled, (state, action) => {
        state.confirmStatus = LoadingStatus.Idle;
        state.result = action.payload;
        state.pendingPlan = null;
        state.selectedTempIds = [];
      })
      .addCase(confirmSchedulePlan.rejected, (state, action) => {
        state.confirmStatus = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to apply the plan";
      });
  },
});

export const { actions, reducer } = assistantScheduleSlice;
export const { togglePlanAction, dismissPlan, resetConversation } = actions;

export const selectAssistantMessages = (state: { assistantSchedule: AssistantScheduleState }) =>
  state.assistantSchedule.messages;
export const selectPendingPlan = (state: { assistantSchedule: AssistantScheduleState }) =>
  state.assistantSchedule.pendingPlan;
export const selectSelectedTempIds = (state: { assistantSchedule: AssistantScheduleState }) =>
  state.assistantSchedule.selectedTempIds;
export const selectPlanResult = (state: { assistantSchedule: AssistantScheduleState }) =>
  state.assistantSchedule.result;
export const selectSendStatus = (state: { assistantSchedule: AssistantScheduleState }) =>
  state.assistantSchedule.sendStatus;
export const selectConfirmStatus = (state: { assistantSchedule: AssistantScheduleState }) =>
  state.assistantSchedule.confirmStatus;
export const selectAssistantStatusMessage = (state: { assistantSchedule: AssistantScheduleState }) =>
  state.assistantSchedule.statusMessage;

export default reducer;
